/**
 * Cosmic CMS API client utility
 * 
 * Provides methods for fetching content from Cosmic CMS on the client side.
 * Uses environment variables for bucket configuration.
 * 
 * Uses the new @cosmicjs/sdk (v3+)
 */

import { createBucketClient } from '@cosmicjs/sdk'

/**
 * Get the Cosmic bucket client instance
 * @returns {Object} Cosmic bucket client
 */
const getCosmicClient = () => {
  const bucketSlug = process.env.GATSBY_COSMIC_BUCKET_SLUG || 'wingspans-production'
  const readKey = process.env.GATSBY_COSMIC_API_KEY
  
  return createBucketClient({
    bucketSlug,
    readKey,
  })
}

/**
 * Fetch a school by slug
 * @param {string} slug - The school slug
 * @param {string} locale - The locale (e.g., 'en-US', 'es-ES')
 * @returns {Promise<Object|null>} The school object or null if not found
 */
export const fetchSchoolBySlug = async (slug, locale = 'en-US') => {
  try {
    const cosmic = getCosmicClient()
    
    // Build the query
    // Note: English content in Cosmic can have locale: null OR locale: "en-US"
    const query = {
      type: 'schools',
      slug: slug,
    }
    
    // For English, we need to handle both null and "en-US" locales
    // For other languages, filter specifically
    if (locale !== 'en-US') {
      query.locale = locale
    }
    
    const { objects } = await cosmic.objects
      .find(query)
      .props('slug,title,type,locale,metadata,id')
      .depth(1)
    
    if (objects && objects.length > 0) {
      // If requesting English, prefer en-US locale over null, otherwise return first match
      if (locale === 'en-US') {
        // First try to find explicit en-US
        const enUSObject = objects.find(obj => obj.locale === 'en-US')
        if (enUSObject) {
          return enUSObject
        }
        
        // Fallback to null locale (default English)
        const nullLocaleObject = objects.find(obj => !obj.locale || obj.locale === null)
        if (nullLocaleObject) {
          return nullLocaleObject
        }
      }
      
      // For Spanish or other languages, return first match
      return objects[0]
    }
    
    return null
  } catch (error) {
    console.error('Error fetching school from Cosmic:', error)
    throw error
  }
}

/**
 * Fetch multiple objects by type
 * @param {string} type - The object type (e.g., 'schools', 'careers')
 * @param {Object} options - Additional query options
 * @returns {Promise<Array>} Array of objects
 */
export const fetchObjectsByType = async (type, options = {}) => {
  try {
    const cosmic = getCosmicClient()
    
    const query = {
      type,
      ...options.query,
    }
    
    const { objects } = await cosmic.objects
      .find(query)
      .props(options.props || 'slug,title,metadata')
      .limit(options.limit || 100)
    
    return objects || []
  } catch (error) {
    console.error(`Error fetching ${type} from Cosmic:`, error)
    throw error
  }
}

/**
 * Fetch student stories for a specific school
 * @param {string} schoolId - The Cosmic object ID of the school
 * @param {string} locale - Locale code ('en-US' or 'es-ES')
 * @returns {Promise<Array>} Array of student story objects
 */
export const fetchStudentStoriesBySchoolId = async (schoolId, locale = 'en-US') => {
  try {
    const cosmic = getCosmicClient()

    const query = {
      type: 'student-stories',
      'metadata.school_reference': schoolId,
    }

    if (locale !== 'en-US') {
      query.locale = locale
    }

    const { objects } = await cosmic.objects
      .find(query)
      .props('slug,title,locale,metadata')
      .depth(1)

    return objects || []
  } catch (error) {
    console.error('Error fetching student stories from Cosmic:', error)
    return []
  }
}

export default {
  fetchSchoolBySlug,
  fetchObjectsByType,
  fetchStudentStoriesBySchoolId,
  getCosmicClient,
}
